import type { RowData } from "@tanstack/react-table";
import type { DataGridRow } from "../columns/types";
import type { EditContext } from "../editing/editActions";
import type { DataGridRowKinds } from "../features/rowKinds";
import type { GridController } from "../focus/gridController";
import type {
    DataGridAtoms,
    DataGridListEditingOptions,
    DataGridTableMeta,
} from "../types";
import type { RowLayout } from "./rowLayout";

export interface RowProps<TData extends RowData> {
    row: DataGridRow<TData>;
    /** Offset inside the body in px. */
    start: number;
    ariaRowIndex: number;
    /** The virtual item index, for `measureRef`. */
    virtualIndex: number;
    /** Set when rows wrap: the virtualizer measures each row's real height. */
    measureRef: ((element: HTMLElement | null) => void) | undefined;
    isExpanded: boolean;
    /** `gridLines: "groups"`: the row closes its top-level group, so it keeps its line. */
    isGroupEnd: boolean;
    layout: RowLayout;
    controller: GridController;
    atoms: DataGridAtoms;
    hasCellNavigation: boolean;
    /** List editing: the row keeps focus, and its navigable cells take it too. */
    hasListEditing: boolean;
    /** The range's corner square that drags to fill. */
    hasFillHandle: boolean;
    hasRowSelection: boolean;
    isTree: boolean;
    /** `parentRows: "summary"`: rows with children render as label rows. */
    hasParentSummaries: boolean;
    /** Editable tables only; stable for the table's lifetime. */
    editContext: EditContext<TData> | null;
    /** List tables only; stable for the table's lifetime. */
    listEditContext: EditContext<TData> | null;
    /** Stable; its attributes, active key, provider, and draft state are read again only when the row's data changes. */
    rowKinds: DataGridRowKinds<TData>;
    /** Compared by identity; cells redraw when it changes. */
    tableMeta: DataGridTableMeta<TData> | undefined;
    /** This render's list editing rules; compared by identity, so cells redraw their access when they change. */
    listEditing: DataGridListEditingOptions<TData> | undefined;
}

/**
 * Re-render only when what the row shows changed, not when the row object
 * did. A search that filters a parent's children changes its chevron and
 * summaries, so the child count counts too. A group row's `original` is its
 * first leaf's, so group rows compare the row itself, which the grouped row
 * model rebuilds whenever any leaf changes.
 */
export const areRowPropsEqual = <TData extends RowData>(
    previous: Readonly<RowProps<TData>>,
    next: Readonly<RowProps<TData>>,
) =>
    previous.row.id === next.row.id &&
    (next.row.getIsGrouped()
        ? previous.row === next.row
        : previous.row.original === next.row.original) &&
    previous.row.depth === next.row.depth &&
    previous.row.subRows.length === next.row.subRows.length &&
    previous.row.getCanExpand() === next.row.getCanExpand() &&
    previous.isExpanded === next.isExpanded &&
    previous.isGroupEnd === next.isGroupEnd &&
    previous.start === next.start &&
    previous.ariaRowIndex === next.ariaRowIndex &&
    previous.virtualIndex === next.virtualIndex &&
    previous.measureRef === next.measureRef &&
    previous.layout === next.layout &&
    previous.controller === next.controller &&
    previous.atoms === next.atoms &&
    previous.hasCellNavigation === next.hasCellNavigation &&
    previous.hasListEditing === next.hasListEditing &&
    previous.hasFillHandle === next.hasFillHandle &&
    previous.hasRowSelection === next.hasRowSelection &&
    previous.isTree === next.isTree &&
    previous.hasParentSummaries === next.hasParentSummaries &&
    previous.editContext === next.editContext &&
    previous.listEditContext === next.listEditContext &&
    previous.rowKinds === next.rowKinds &&
    previous.tableMeta === next.tableMeta &&
    previous.listEditing === next.listEditing;
